import React, { useCallback, useEffect, useMemo, useState } from "react";
import ScrollAnimation from "../../shared/ScrollAnimation/ScrollAnimation";

const initialFormData = {
  name: "",
  email: "",
  message: "",
  number_of_people: "0",
  tour_date: "0",
};

const initialErrors = {
  name_error: "",
  email_error: "",
  message_error: "",
  tour_date_error: "",
  number_of_people_error: "",
};

export default function StadiumTour() {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);

  const [tourDates, setTourDates] = useState([]);
  const [tourDatesLoading, setTourDatesLoading] = useState(false);
  const [tourDatesError, setTourDatesError] = useState(false);

  const [isLoaderActive, setIsLoaderActive] = useState(false);
  const [messageSendError, setMessageSendError] = useState(null);

  const numberOfPeopleOptions = useMemo(() => {
    return Array.from({ length: 20 }, (_, index) => index + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchTourDates = async () => {
      setTourDatesLoading(true);
      setTourDatesError(false);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_AUTH_BASE_URL}/stadium-tour/dates`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        const json = await response.json();

        if (!response.ok) {
          throw new Error("Nem sikerült lekérni a túraidőpontokat.");
        }

        const rawDates = Array.isArray(json)
          ? json
          : json.data || json.tour_dates || [];

        setTourDates(rawDates);
      } catch (error) {
        if (error.name !== "AbortError") {
          setTourDatesError(true);
        }
      } finally {
        setTourDatesLoading(false);
      }
    };

    fetchTourDates();

    return () => {
      controller.abort();
    };
  }, []);

  const handleChange = useCallback((event) => {
    const { id, type, checked, value } = event.target;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [id]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const validate = useCallback(() => {
    let isValid = true;

    const nextErrors = {
      name_error: "",
      email_error: "",
      message_error: "",
      tour_date_error: "",
      number_of_people_error: "",
    };

    if (formData.message.trim().length < 5) {
      nextErrors.message_error = "Túl rövid az üzenet!";
      isValid = false;
    }

    if (formData.name.trim().length < 3) {
      nextErrors.name_error = "Helytelen név!";
      isValid = false;
    }

    const emailRegex =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (!emailRegex.test(String(formData.email).toLowerCase())) {
      nextErrors.email_error = "Helytelen e-mail cím!";
      isValid = false;
    }

    if (formData.number_of_people === "0") {
      nextErrors.number_of_people_error = "Válassz ki egy tervezett létszámot!";
      isValid = false;
    }

    if (formData.tour_date === "0") {
      nextErrors.tour_date_error = "Válassz ki egy túra időpontot!";
      isValid = false;
    }

    setErrors(nextErrors);

    return isValid;
  }, [formData]);

  const saveRegisterData = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_AUTH_BASE_URL}/stadium-tour/store`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const json = await response.json();

      if (json.success === true) {
        setMessageSendError(false);
      } else {
        setMessageSendError(true);
      }
    } catch (error) {
      setMessageSendError(true);
    } finally {
      setIsLoaderActive(false);
    }
  }, [formData]);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();

      setIsLoaderActive(true);
      setMessageSendError(null);

      const isValid = validate();

      if (!isValid) {
        setIsLoaderActive(false);
        return;
      }

      saveRegisterData();
    },
    [validate, saveRegisterData]
  );

  const getTourDateValue = (tourDate) => {
    if (typeof tourDate === "string") {
      return tourDate;
    }

    return tourDate.value || tourDate.id || tourDate.date || "";
  };

  const getTourDateLabel = (tourDate) => {
    if (typeof tourDate === "string") {
      return tourDate;
    }

    return tourDate.label + ' | ' + tourDate.tour_datetime;
  };

  return (
    <div>
      <section id="stadium-tour" className="stadium-tour-container">
        <div className="container">
          <ScrollAnimation animateIn="fadeIn" duration={1.5} animateOnce>
            <h2>Stadiontúra</h2>
          </ScrollAnimation>

          <ScrollAnimation animateIn="fadeIn" delay={250} duration={1.5} animateOnce>
            <p>
              A legendás Lipták Grund környékén már több, mint száztíz éve rúgják a
              labdát a kispesti focisták. Stadiontúránk keretében megismerhetik
              kispesti pályák és a Honvéd történelmének kevesek által ismert
              érdekességeit, a Bozsik Aréna építésével kapcsolatos adatokat és
              információkat, meghallgathatják az első magyar ultracsoport otthonának,
              a legendás Északi Kanyarnak a történetét, megnézhetik a stadion
              mérkőzésnapokon sem látogatható részeit, sőt még a pálya szélére is
              bejuthatnak! A program ára felnőtteknek 2.000 Ft/fő, partnerklubok részére
              1.000 Ft/fő, 3 éves kor alatt ingyenes, családi belépő 1.500 Ft/fő (2 szülő + 1 gyermek).
              Egy stadiontúrán a maximális létszám 40 fő és minimum 20 fő esetén indul el.<br/>
              Jelentkezni az alábbi űrlap kitöltésével tudnak, sok szeretettel várjuk
              Önöket!
            </p>
          </ScrollAnimation>

          <ScrollAnimation animateIn="fadeInRightBig" animateOnce>
            <form onSubmit={handleSubmit}>
              {messageSendError === false && (
                <div className="form-group">
                  <div className="success_message">Sikeres jelentkezés!</div>
                </div>
              )}

              {messageSendError === true && (
                <div className="form-group">
                  <div className="error_message">
                    Hiba történt a jelentkezés elküldése közben.
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group col-6">
                  <label htmlFor="name">
                    Név <span>*</span>
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                  />

                  <div className="validation_error">{errors.name_error}</div>
                </div>

                <div className="form-group col-6">
                  <label htmlFor="email">
                    E-mail cím <span>*</span>
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                  />

                  <div className="validation_error">{errors.email_error}</div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-6">
                  <label htmlFor="number_of_people">
                    Tervezett létszám <span>*</span>
                  </label>

                  <select
                    id="number_of_people"
                    className="form-control"
                    value={formData.number_of_people}
                    onChange={handleChange}
                  >
                    <option value="0">válassz!</option>

                    {numberOfPeopleOptions.map((numberOfPeople) => (
                      <option key={numberOfPeople} value={numberOfPeople}>
                        {numberOfPeople} fő
                      </option>
                    ))}
                  </select>

                  <div className="validation_error">{errors.number_of_people_error}</div>
                </div>

                <div className="form-group col-6">
                  <label htmlFor="tour_date">
                    Választott időpont <span>*</span>
                  </label>

                  <select
                    id="tour_date"
                    className="form-control"
                    value={formData.tour_date}
                    onChange={handleChange}
                    disabled={tourDatesLoading}
                  >
                    <option value="0">
                      {tourDatesLoading ? "Időpontok betöltése..." : "válassz!"}
                    </option>

                    {tourDates.map((tourDate) => {
                      const value = getTourDateValue(tourDate);
                      const label = getTourDateLabel(tourDate);

                      return (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      );
                    })}
                  </select>

                  <div className="validation_error">{errors.tour_date_error}</div>

                  {tourDatesError && (
                    <div className="validation_error">
                      Nem sikerült betölteni a túraidőpontokat.
                    </div>
                  )}
                </div>

                <div className="form-group col-12">
                  <label htmlFor="message">
                    Üzenet <span>*</span>
                  </label>

                  <textarea
                    className="form-control"
                    id="message"
                    value={formData.message}
                    onChange={handleChange}
                  />

                  <div className="validation_error">{errors.message_error}</div>
                </div>

                <div className="form-group col-12">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoaderActive === true}
                  >
                    {isLoaderActive ? "Küldés..." : "Küldés"}
                  </button>
                </div>
              </div>
            </form>
          </ScrollAnimation>
        </div>
      </section>
    </div>
  );
}
